// Copyright 2026, Imprivata, Inc.  All rights reserved.

import {
  createOidcAppIntegration,
  deleteOidcAppIntegration,
  findOidcAppIntegrations,
  updateOidcAppIntegration,
} from '@api/portalServices';
import type {
  OidcAppIntegration,
  OidcAppIntegrationCreateResponse,
} from '@api/types';
import { createCodingCtxV2 } from '@api/utils';
import type { ApiError } from '@errorHandler/errors';
import { QueryKey } from '@portal/constants';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { EPIC_HYPERDRIVE_PRODUCT_ID, OIDC_IDP_FLOW } from '../constants';

const QUERY_KEY = (tenantId: string) => [QueryKey.OIDC_APP_CONFIG, tenantId, EPIC_HYPERDRIVE_PRODUCT_ID];

export const normalizeUris = (uris: string[]): string[] => [...new Set(uris.map(u => u.trim()).filter(Boolean))];

export interface CreateOidcResult {
  createResponse: OidcAppIntegrationCreateResponse;
  decryptedSecret: string;
}

export const useOidcAppConfig = (tenantId: string | undefined) => {
  const queryClient = useQueryClient();
  const tid = tenantId ?? '';

  const { data, isLoading, error } = useQuery<OidcAppIntegration | null, ApiError>({
    queryKey: QUERY_KEY(tid),
    queryFn: async () => {
      const res = await findOidcAppIntegrations({
        oidcIdpFlow: OIDC_IDP_FLOW,
        productId: EPIC_HYPERDRIVE_PRODUCT_ID,
      });
      return res.oidcAppIntegrations[0] ?? null;
    },
    enabled: Boolean(tenantId),
    staleTime: 0,
    retry: false,
  });

  const invalidate = () =>
    queryClient.invalidateQueries({ queryKey: QUERY_KEY(tid) });

  const { mutateAsync: createOidcConfig, reset: resetCreate } = useMutation({
    mutationFn: async (args: { uris: string[]; displayName: string }): Promise<CreateOidcResult> => {
      const codingCtx = await createCodingCtxV2();
      const createResponse = await createOidcAppIntegration(
        {
          displayName: args.displayName,
          redirectUris: normalizeUris(args.uris),
          productId: EPIC_HYPERDRIVE_PRODUCT_ID,
        },
        codingCtx,
      );
      const decryptedSecret = codingCtx.decryptToString(createResponse.clientSecret);
      return { createResponse, decryptedSecret };
    },
    onSuccess: () => { void invalidate(); },
    retry: false,
  });

  const { mutateAsync: updateOidcConfig } = useMutation({
    mutationFn: (args: { clientId: string; uris: string[] }) =>
      updateOidcAppIntegration({
        clientId: args.clientId,
        redirectUris: normalizeUris(args.uris),
      }),
    onSuccess: () => { void invalidate(); },
    retry: false,
  });

  const { mutateAsync: deleteOidcConfig } = useMutation({
    mutationFn: (clientId: string) => deleteOidcAppIntegration({ clientId }),
    onSuccess: () => {
      queryClient.setQueryData(QUERY_KEY(tid), null);
      void invalidate();
    },
    retry: false,
  });

  return {
    oidcConfig: data,
    isLoading,
    error,
    createOidcConfig: (uris: string[], displayName: string) =>
      createOidcConfig({ uris, displayName }),
    resetCreateOidcConfig: resetCreate,
    updateOidcConfig: (clientId: string, uris: string[]) =>
      updateOidcConfig({ clientId, uris }),
    deleteOidcConfig,
  };
};
